import { App, Component } from 'vue';

import { createCoreVueApp } from '@/entrypoints/helpers/core-vue-app';

/**
 * Wait for container to be available in the DOM with retry logic
 * This handles race conditions when HTML is being replaced
 */
export const waitForContainer = (
  selector: string,
  maxRetries = 20,
  retryDelay = 50
): Promise<HTMLElement | null> => {
  return new Promise((resolve) => {
    let retries = 0;

    const checkContainer = () => {
      const container = document.querySelector<HTMLElement>(selector);
      if (container) {
        resolve(container);
        return;
      }

      retries++;
      if (retries >= maxRetries) {
        resolve(null);
        return;
      }

      setTimeout(checkContainer, retryDelay);
    };

    checkContainer();
  });
};

/**
 * Check if container already has a Vue app mounted
 * Returns true if app is already mounted (should skip initialization), false otherwise
 */
type ContainerWithVueApp = HTMLElement & {
  __vueParentComponent?: unknown;
  __vue_app__?: unknown;
};

export const hasExistingVueApp = (
  container: HTMLElement,
  containerId: string,
  mountedApps: Map<string, App>
): boolean => {
  const containerWithApp = container as ContainerWithVueApp;

  if (containerWithApp.__vueParentComponent || containerWithApp.__vue_app__) {
    const existingApp = mountedApps.get(containerId);
    if (existingApp) {
      return true;
    }
  }

  return false;
};

export type Scenario = 'none' | 'shipping_only' | 'dual_threshold';

export type ComponentState = {
  isInitializing: boolean;
  hasPendingRequest: boolean;
};

export type InitializationOptions<
  TProps extends Record<string, unknown> = Record<string, unknown>
> = {
  containerId: string;
  mountSelector: string;
  component: Component;
  state: ComponentState;
  mountedApps: Map<string, App>;
  waitForCartUpdate: boolean;
  extractProps: (
    container: HTMLElement
  ) => TProps | null | Promise<TProps | null>;
  onError?: (error: unknown) => void;
  onBeforeMount?: (props: TProps) => void;
  preMountCheck?: () => boolean | Promise<boolean>;
  onUnmount?: (app: App) => void;
  shouldProcessPendingRequest?: (
    containerId: string,
    mountedApps: Map<string, App>
  ) => boolean;
};

/**
 * Generic initialization helper for cart components
 * Handles common patterns: state management, container waiting, app detection, mounting
 */
export const initializeComponent = async <
  TProps extends Record<string, unknown> = Record<string, unknown>
>(
  options: InitializationOptions<TProps>
): Promise<void> => {
  const {
    containerId,
    mountSelector,
    component,
    state,
    mountedApps,
    waitForCartUpdate,
    extractProps,
    onError,
    onBeforeMount,
    preMountCheck,
    onUnmount,
    shouldProcessPendingRequest
  } = options;

  if (state.isInitializing) {
    state.hasPendingRequest = true;
    return;
  }

  state.isInitializing = true;
  state.hasPendingRequest = false;

  try {
    if (preMountCheck) {
      const shouldContinue = await preMountCheck();
      if (!shouldContinue) {
        return;
      }
    }

    const container = waitForCartUpdate
      ? await waitForContainer(mountSelector)
      : document.querySelector<HTMLElement>(mountSelector);

    if (!container) {
      const existingApp = mountedApps.get(containerId);
      if (existingApp) {
        if (onUnmount) {
          onUnmount(existingApp);
        } else {
          existingApp.unmount();
        }
        mountedApps.delete(containerId);
      }
      return;
    }

    if (hasExistingVueApp(container, containerId, mountedApps)) {
      return;
    }

    const existingApp = mountedApps.get(containerId);
    if (existingApp) {
      if (onUnmount) {
        onUnmount(existingApp);
      } else {
        existingApp.unmount();
      }
      mountedApps.delete(containerId);
    }

    const props = await extractProps(container);
    if (props === null) {
      return;
    }

    onBeforeMount?.(props);

    const app = await createCoreVueApp(component, container, props);

    if (hasExistingVueApp(container, containerId, mountedApps)) {
      return;
    }

    app.mount(container);
    mountedApps.set(containerId, app);
  } catch (error) {
    if (onError) {
      onError(error);
    } else {
      console.error(`Failed to initialize ${containerId}:`, error);
    }
  } finally {
    state.isInitializing = false;

    // If a request came in while we were initializing, process it
    if (state.hasPendingRequest) {
      state.hasPendingRequest = false;

      const shouldProcess =
        shouldProcessPendingRequest === undefined
          ? true
          : shouldProcessPendingRequest(containerId, mountedApps);

      if (shouldProcess) {
        await initializeComponent(options);
      }
    }
  }
};
